import { safeAuthFetch } from '../utils/safeFetch';
import { API_BASE_URL } from '../config/api.js';

// Issue #981: testing-panel sessions can be superseded/completed/cancelled by
// a sibling tab while a result submission is in flight. The backend correctly
// returns 400 with body `{ error: "Session is <status>, cannot submit results" }`
// (see backend/src/services/testingService.js). This is expected behaviour, not
// a bug — surfacing it as a Sentry issue is noise. We detect that specific
// error here, log a console.warn so the failure is still visible to the
// developer, and re-throw so the caller's existing error handling still runs.
// The error is annotated with `expectedSupersededSession = true` so Sentry's
// `beforeSend` (instrument.js) can filter it out of capture.
const EXPECTED_TESTING_SESSION_ERROR = /Session is (superseded|completed|cancelled), cannot submit results/;

/**
 * Generic API request helper.
 * @param {string} endpoint - API endpoint (e.g., '/api/avatars')
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  let response;
  try {
    response = await safeAuthFetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
  } catch (err) {
    // Suppress Sentry capture for expected superseded-session errors (Issue #981).
    // The FetchError carries the parsed response body on `err.body`, plus the
    // raw text on `err.responseBody`. Match against either to be defensive.
    const errorMessage =
      (err && typeof err.body === 'object' && err.body !== null && typeof err.body.error === 'string'
        ? err.body.error
        : null) ||
      (err && typeof err.responseBody === 'string' ? err.responseBody : '');
    if (EXPECTED_TESTING_SESSION_ERROR.test(errorMessage)) {
      // eslint-disable-next-line no-console -- intentional: replaces Sentry capture for expected error
      console.warn('[apiRequest] Expected testing-session error (suppressed from Sentry):', errorMessage);
      try {
        if (err && typeof err === 'object') {
          err.expectedSupersededSession = true;
        }
      } catch {
        // Never break the throw on annotation failure.
      }
    }
    // Always re-throw — caller still handles it.
    throw err;
  }

  // 204 No Content and empty-body 2xx responses must not hit response.json()
  // (which would throw SyntaxError on empty input).
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};
